/**
 * Automatic client telemetry (docs/UI.md "Report a problem", docs/ONLINE.md "Problem reports"): uncaught errors,
 * unhandled promise rejections, WebGL context loss and a sustained low frame rate are reported to the server on
 * their own, without the player doing anything.
 *
 * Polite by construction:
 *  - de-duplicated: the first time a problem is seen it goes out (after a short batching delay); repeats are only
 *    counted and sent as one `count` at most once a minute;
 *  - rate limited: at most AUTO_MAX_PER_SESSION reports a page load, context losses at most 3, low fps once;
 *  - no personal data: the message, the stack, the build and the device context (no log lines, no chat, no names;
 *    the server ties it to the account when the player is signed in, nothing more).
 * Pure: the caller wires the browser events and the clock (feedbackSystem.ts); unit-tested in feedback.test.ts.
 */
import type { ReportContext, ReportKind, ReportPayload } from '../../shared/feedback';

export const AUTO_MAX_PER_SESSION = 20;
export const AUTO_BATCH_MS = 2_000;
export const AUTO_REPEAT_MS = 60_000;
export const CONTEXT_LOSS_MAX = 3;
/** Low frame rate: average below LOW_FPS over LOW_FPS_WINDOW_S of play (visible page, not paused). */
export const LOW_FPS = 18;
export const LOW_FPS_WINDOW_S = 30;

interface Pending {
  kind: ReportKind;
  message: string;
  stack?: string;
  /** Occurrences not yet reported. */
  count: number;
  /** When it may go out next (ms). */
  dueAt: number;
  sentOnce: boolean;
}

/** Messages that are noise, not bugs (browser quirks, extensions, aborted fetches when leaving). */
const IGNORED = [/ResizeObserver loop/i, /^Script error\.?$/i, /AbortError/i, /chrome-extension:|moz-extension:|safari-extension:/i];

export class AutoReporter {
  private readonly pending = new Map<string, Pending>();
  private sent = 0;
  private contextLosses = 0;
  private lowFpsSent = false;
  private fpsWindow: { sum: number; n: number; s: number } = { sum: 0, n: 0, s: 0 };
  private sending = false;

  constructor(
    private readonly send: (p: ReportPayload) => void,
    private readonly context: () => ReportContext,
    private readonly now: () => number,
  ) {}

  /** Reports sent this page load. */
  get count(): number {
    return this.sent;
  }

  error(kind: 'error' | 'rejection', message: string, stack?: string): void {
    const msg = (message || 'unknown error').slice(0, 500);
    if (IGNORED.some((re) => re.test(msg) || (stack ? re.test(stack.split('\n')[1] ?? '') : false))) return;
    // Our own reporting failing must never report itself (a loop).
    if (this.sending) return;
    const key = `${kind}|${msg.replace(/\d+/g, '#')}|${(stack ?? '').split('\n')[1]?.trim() ?? ''}`;
    const p = this.pending.get(key);
    if (p) {
      p.count++;
      return;
    }
    const entry: Pending = { kind, message: msg, count: 1, dueAt: this.now() + AUTO_BATCH_MS, sentOnce: false };
    if (stack) entry.stack = stack;
    this.pending.set(key, entry);
  }

  contextLost(): void {
    if (this.contextLosses >= CONTEXT_LOSS_MAX) return;
    this.contextLosses++;
    this.emit({ kind: 'webgl', message: `WebGL context lost (${this.contextLosses} this session)` });
  }

  /**
   * One frame-rate sample (frames per second, averaged by the caller over `dtS` seconds). Only counts while the
   * player is actually playing on a visible page; a pause or a hidden tab starts the window again.
   */
  fps(fps: number, dtS: number, playing: boolean): void {
    if (this.lowFpsSent) return;
    if (!playing || !(fps > 0)) {
      this.fpsWindow = { sum: 0, n: 0, s: 0 };
      return;
    }
    const w = this.fpsWindow;
    w.sum += fps * dtS;
    w.s += dtS;
    w.n++;
    if (w.s < LOW_FPS_WINDOW_S) return;
    const avg = w.sum / w.s;
    this.fpsWindow = { sum: 0, n: 0, s: 0 };
    if (avg >= LOW_FPS) return;
    this.lowFpsSent = true;
    this.emit({ kind: 'lowfps', message: `Low frame rate: ${avg.toFixed(1)} fps on average over ${LOW_FPS_WINDOW_S} s of play` });
  }

  /** Send what is due (call every second or so). */
  tick(): void {
    const t = this.now();
    for (const [key, p] of this.pending) {
      if (t < p.dueAt || p.count === 0) continue;
      const payload: ReportPayload = { kind: p.kind, message: p.message, count: p.count };
      if (p.stack) payload.stack = p.stack;
      p.count = 0;
      p.sentOnce = true;
      p.dueAt = t + AUTO_REPEAT_MS;
      if (!this.emit(payload)) {
        this.pending.delete(key);
        continue;
      }
    }
  }

  private emit(p: Omit<ReportPayload, 'context' | 'at'>): boolean {
    if (this.sent >= AUTO_MAX_PER_SESSION) return false;
    this.sent++;
    this.sending = true;
    try {
      this.send({ ...p, at: Date.now(), context: this.context() });
    } catch {
      /* the transport failed synchronously: nothing to do */
    } finally {
      this.sending = false;
    }
    return true;
  }
}
